<template>
    <v-container fluid>
        <page-header title="Requests" icon="mdi-bell-outline" />
        <v-card class="rounded-lg" elevation="1">
            <v-tabs v-model="tab" color="primary darken-1">
                <v-tab><v-icon small left>mdi-swap-horizontal</v-icon>Match changes</v-tab>
                <v-tab><v-icon small left>mdi-calendar-remove-outline</v-icon>Postponements</v-tab>
            </v-tabs>
            <v-divider />
            <div class="pa-4">
                <v-select
                    v-model="status"
                    :items="statusItems"
                    item-text="label"
                    item-value="value"
                    label="Status"
                    outlined
                    dense
                    hide-details
                    clearable
                    class="requests__status"
                />
            </div>

            <!-- match changes -->
            <v-data-table
                v-if="tab === 0"
                :headers="changeHeaders"
                :items="items"
                :options.sync="options"
                :server-items-length="total"
                :loading="loading"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                must-sort
                class="requests__table"
            >
                <template v-slot:item.createdAt="{ item }">{{ formatMatchDate(item.createdAt) }}</template>
                <template v-slot:item.request="{ item }">
                    <match-change-summary :request="item" class="py-2" />
                </template>
                <template v-slot:item.status="{ item }">
                    <v-chip small :color="statusOf(item).color" text-color="white">{{ statusOf(item).label }}</v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div v-if="item.status === 'pending'" class="d-flex">
                        <v-btn x-small depressed color="black" class="white--text mr-2" @click="review('change', item, true)">Approve</v-btn>
                        <v-btn x-small outlined color="red darken-1" @click="review('change', item, false)">Reject</v-btn>
                    </div>
                </template>
            </v-data-table>

            <!-- postponements -->
            <v-data-table
                v-else
                :headers="postponementHeaders"
                :items="items"
                :options.sync="options"
                :server-items-length="total"
                :loading="loading"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                must-sort
                class="requests__table"
            >
                <template v-slot:item.createdAt="{ item }">{{ formatMatchDate(item.createdAt) }}</template>
                <template v-slot:item.match="{ item }">
                    <div class="py-2">
                        <div class="font-weight-bold">{{ item.team1Name }} - {{ item.team2Name }}</div>
                        <div class="text-caption grey--text text--darken-1">
                            {{ formatMatchDate(item.matchDatetime) }}<span v-if="item.arenaName"> · {{ item.arenaName }}</span>
                        </div>
                        <div v-if="item.reason" class="text-caption grey--text text--darken-2">"{{ item.reason }}"</div>
                    </div>
                </template>
                <template v-slot:item.team="{ item }">
                    {{ item.teamName }}<span v-if="item.playerName" class="grey--text"> ({{ item.playerName }})</span>
                </template>
                <template v-slot:item.status="{ item }">
                    <v-chip small :color="statusOf(item).color" text-color="white">{{ statusOf(item).label }}</v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div v-if="item.status === 'pending'" class="d-flex">
                        <v-btn x-small depressed color="black" class="white--text mr-2" @click="review('postponement', item, true)">Approve</v-btn>
                        <v-btn x-small outlined color="red darken-1" @click="review('postponement', item, false)">Reject</v-btn>
                    </div>
                </template>
            </v-data-table>
        </v-card>

        <request-review-dialog ref="review" @done="onReviewed" />
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import Alert from '@/components/Alert'
    import PageHeader from '@/components/PageHeader'
    import MatchChangeSummary from '@/components/MatchChangeSummary'
    import RequestReviewDialog from '@/components/RequestReviewDialog'
    import postponementService from '@/services/postponement'
    import matchChangeService from '@/services/match-change'
    import { MATCH_CHANGE_STATUS, POSTPONEMENT_STATUS, errorMessage, formatMatchDate } from '@/utils/requests'

    const DEFAULT_OPTIONS = { page: 1, itemsPerPage: 10, sortBy: ['createdAt'], sortDesc: [true] }

    // The postponement and match change requests of the team captains, with their decision
    export default {
        name: 'Requests',
        components: { Alert, PageHeader, MatchChangeSummary, RequestReviewDialog },
        data() {
            return {
                tab: 0,
                status: null,
                items: [],
                total: 0,
                loading: false,
                options: { ...DEFAULT_OPTIONS },
                showAlert: false,
                message: null,
                type: null,
                changeHeaders: [
                    { text: 'Requested', value: 'createdAt', width: 150 },
                    { text: 'Matches & votes', value: 'request', sortable: false },
                    { text: 'Status', value: 'status', width: 190 },
                    { text: '', value: 'actions', sortable: false, width: 170 }
                ],
                postponementHeaders: [
                    { text: 'Requested', value: 'createdAt', width: 150 },
                    { text: 'Match', value: 'match', sortable: false },
                    { text: 'Team', value: 'team', sortable: false },
                    { text: 'Status', value: 'status', width: 190 },
                    { text: '', value: 'actions', sortable: false, width: 170 }
                ]
            }
        },
        computed: {
            statuses() {
                return this.tab === 0 ? MATCH_CHANGE_STATUS : POSTPONEMENT_STATUS
            },
            statusItems() {
                return Object.keys(this.statuses).map(value => ({ value, label: this.statuses[value].label }))
            }
        },
        watch: {
            tab() {
                this.status = null
                // a new options object: its watcher fetches the items
                this.options = { ...DEFAULT_OPTIONS }
            },
            status() {
                if (this.options.page !== 1) {
                    this.options = { ...this.options, page: 1 }
                } else {
                    this.fetchItems()
                }
            },
            options: {
                deep: true,
                handler() {
                    this.fetchItems()
                }
            }
        },
        created() {
            this.$root.$on('requests-changed', this.fetchItems)
        },
        beforeDestroy() {
            this.$root.$off('requests-changed', this.fetchItems)
        },
        methods: {
            formatMatchDate,
            statusOf(item) {
                return this.statuses[item.status] || { label: item.status, color: 'grey' }
            },
            fetchItems() {
                const filter = this.status ? { status: this.status } : {}
                const sort = { sortBy: this.options.sortBy[0] || 'createdAt', sortDesc: this.options.sortDesc[0] !== false }
                const call = this.tab === 0
                    ? matchChangeService.getFilteredMatchChanges(filter, this.options.page, this.options.itemsPerPage, sort)
                    : postponementService.getFilteredPostponements(filter, this.options.page, this.options.itemsPerPage, sort)
                this.loading = true
                call.then(resp => {
                    this.items = resp.data.content
                    this.total = resp.data.totalElements
                }).catch(error => {
                    this.setAlert(errorMessage(error), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            review(type, item, approve) {
                this.$refs.review.open(type, item, approve)
            },
            onReviewed({ ok, message }) {
                this.setAlert(message, ok ? 'success' : 'error')
                // refreshes this list and the topbar notifications
                this.$root.$emit('requests-changed')
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            }
        }
    }
</script>

<style scoped>
.requests__status {
    max-width: 280px;
}

.requests__table .v-btn {
    text-transform: none;
    letter-spacing: normal;
}
</style>
